import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { apiErrorMessage } from '@/lib/clients';
import { memberName, useDeleteMember, type Member } from '@/lib/users';

const CONFIRM_WORD = 'SUPPRIMER';

interface Props { member: Member | null; onOpenChange: (open: boolean) => void }

/** Retrait du membre de l'organisation : confirmation par saisie (action destructive). */
export function DeleteMemberDialog({ member, onOpenChange }: Props) {
  const remove = useDeleteMember();
  const [typed, setTyped] = useState('');

  const resetMutation = remove.reset;
  useEffect(() => {
    if (member) { setTyped(''); resetMutation(); }
  }, [member, resetMutation]);

  const confirm = () => {
    if (!member || typed !== CONFIRM_WORD) return;
    remove.mutate(member.membershipId, {
      onSuccess: () => {
        toast.success(`${memberName(member)} a été retiré de l’organisation`);
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={!!member} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Supprimer le membre</DialogTitle>
          {member && (
            <DialogDescription>
              {memberName(member)} ({member.email}) perdra immédiatement l&apos;accès à l&apos;organisation.
            </DialogDescription>
          )}
        </DialogHeader>
        <div>
          <Label htmlFor="delete-confirm">Saisir {CONFIRM_WORD} pour confirmer</Label>
          <Input id="delete-confirm" autoComplete="off" value={typed} onChange={(e) => setTyped(e.target.value)} />
        </div>
        {remove.isError && (
          <p role="alert" className="text-sm text-danger">{apiErrorMessage(remove.error, 'Erreur lors de la suppression')}</p>
        )}
        <DialogFooter>
          <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button variant="destructive" onClick={confirm} disabled={typed !== CONFIRM_WORD || remove.isPending}>
            {remove.isPending ? 'Suppression…' : 'Supprimer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
